import React, { createContext, useContext, ReactNode } from "react";
import { useProfileState } from "@/hooks/useProfileState";
import type { UserProfile } from "@/types/profile";

interface ProfileContextType {
  profile: UserProfile;
  updateProfile: (field: keyof UserProfile, value: any) => Promise<void>;
  updateContact: (field: 'business_email' | 'personal_email' | 'business_phone' | 'personal_phone', value: string) => Promise<void>;
  updateAddress: (type: 'home_address' | 'work_address', field: string, value: string) => Promise<void>;
  resetProfile: () => void;
  isLoading: boolean;
  error: Error | null;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export function ProfileProvider({ children }: { children: ReactNode }) {
  const profileState = useProfileState();
  
  return (
    <ProfileContext.Provider value={profileState as ProfileContextType}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
}

export function useUserProfile() {
  const { profile } = useProfile();
  
  const displayName = profile?.preferred_first_name || profile?.legal_first_name || 'User';
  const fullName = profile ? `${profile.legal_first_name || ''} ${profile.legal_last_name || ''}`.trim() : '';
  const email = profile?.business_email || '';
  
  return {
    displayName,
    fullName,
    email,
    profile
  };
}